/**
 * ScoreEditor — the Voice Line Configurator (DESIGN_SPEC §4.4, v1 form shape).
 *
 * Form-style editor for a single Score draft (name + description + content).
 * Validation: name required after trim. Read-only mode renders all fields
 * with `readOnly` and a paper-cream hint banner.
 */

import { useId } from "react";

import { scoreEditorCopy } from "./ScoreEditor.copy";

export interface ScoreDraft {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly content: string;
}

export interface ScoreEditorProps {
  readonly score: ScoreDraft;
  readonly onChange: (draft: ScoreDraft) => void;
  readonly readOnly?: boolean;
}

const labelStyle: React.CSSProperties = {
  fontSize: "0.781rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
};

const inputStyle: React.CSSProperties = {
  background: "var(--rehearsal-taupe)",
  border: "1px solid var(--measure-line)",
  borderRadius: "var(--radius)",
  padding: "0.5rem 0.75rem",
  fontSize: "0.875rem",
};

const textareaStyle: React.CSSProperties = {
  background: "var(--paper-cream)",
  color: "var(--score-ink)",
  border: "1px solid var(--measure-line)",
  borderRadius: "var(--radius)",
  padding: "0.75rem",
  fontSize: "0.875rem",
  lineHeight: 1.55,
  resize: "vertical",
};

export function ScoreEditor({
  score,
  onChange,
  readOnly = false,
}: ScoreEditorProps): React.ReactElement {
  const nameId = useId();
  const descId = useId();
  const contentId = useId();
  const errorId = useId();

  const nameError = score.name.trim().length === 0 ? scoreEditorCopy.nameRequiredError : null;
  const update = (patch: Partial<ScoreDraft>): void => onChange({ ...score, ...patch });

  return (
    <form
      data-testid="score-editor"
      aria-label="score editor"
      className="bg-stage-riser border-ledger-line rounded-ledger-line shadow-lamp-warm"
      style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.875rem" }}
      onSubmit={(e): void => e.preventDefault()}
    >
      {readOnly ? (
        <p
          data-testid="score-editor-readonly-hint"
          className="font-plex-mono"
          style={{ margin: 0, padding: "0.5rem 0.75rem", background: "var(--paper-cream)", color: "var(--score-ink)", fontSize: "0.781rem", borderRadius: "var(--radius)" }}
        >
          {scoreEditorCopy.readOnlyHint}
        </p>
      ) : null}

      <label htmlFor={nameId} className="font-plex-mono text-paper-cream-dim" style={labelStyle}>
        {scoreEditorCopy.nameLabel}
        <input
          id={nameId}
          data-testid="score-editor-name"
          type="text"
          value={score.name}
          readOnly={readOnly}
          required
          placeholder={scoreEditorCopy.namePlaceholder}
          aria-invalid={nameError !== null}
          aria-describedby={nameError !== null ? errorId : undefined}
          onChange={(e): void => update({ name: e.target.value })}
          className="font-sohne-prose text-paper-cream"
          style={inputStyle}
        />
        {nameError !== null && !readOnly ? (
          <span
            id={errorId}
            role="alert"
            data-testid="score-editor-name-error"
            style={{ color: "var(--dynamic-forte)", fontSize: "0.781rem" }}
          >
            {nameError}
          </span>
        ) : null}
      </label>

      <label htmlFor={descId} className="font-plex-mono text-paper-cream-dim" style={labelStyle}>
        {scoreEditorCopy.descriptionLabel}
        <input
          id={descId}
          data-testid="score-editor-description"
          type="text"
          value={score.description}
          readOnly={readOnly}
          placeholder={scoreEditorCopy.descriptionPlaceholder}
          onChange={(e): void => update({ description: e.target.value })}
          className="font-sohne-prose text-paper-cream"
          style={inputStyle}
        />
      </label>

      <label htmlFor={contentId} className="font-plex-mono text-paper-cream-dim" style={labelStyle}>
        {scoreEditorCopy.contentLabel}
        <textarea
          id={contentId}
          data-testid="score-editor-content"
          value={score.content}
          readOnly={readOnly}
          placeholder={scoreEditorCopy.contentPlaceholder}
          rows={10}
          onChange={(e): void => update({ content: e.target.value })}
          className="font-plex-mono"
          style={textareaStyle}
        />
      </label>
    </form>
  );
}

export default ScoreEditor;
